import { useContext, useEffect, useRef, useState } from "react";
import { Winner } from "./types";
import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
import styles from "./winners.module.css";
import { useAppSelector } from "../../redux/hooks";
import "react-vertical-timeline-component/style.min.css";
import InfiniteScroll from "react-infinite-scroll-component";
import TimelineElement from "./timelineElement";
import { WINNERS_PER_PAGE } from "./winners-constants";
import { PrizeCollectionWinner } from "../../collections/prize-collections/types";
import { CurrentPageContext } from "./winners-context";
import React from "react";
import { hasActiveSubscription } from "../../../helpers/hasActiveSubscription";

const WinnersTimeline = () => {
  const isWinnersPage = useContext(CurrentPageContext);
  const filteredList = useAppSelector(state => state.winners.filteredList);
  const filteredPrizeCollectionList = useAppSelector(state => state.winners.filteredPrizeCollectionList);
  const winners = isWinnersPage ? filteredList : filteredPrizeCollectionList;
  const [clickedWinnerIndex, setClickedWinnerIndex] = useState(-1);
  const winnersStartIndex = useRef(0);
  const winnersEndIndex = useRef(WINNERS_PER_PAGE);
  const [winnersInView, setWinnersInView] = useState<Winner[]>([]);
  const [hasAdsToLoad, setHasAdsToLoad] = useState(true);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setClickedWinnerIndex(-1);
    winnersStartIndex.current = 0;
    winnersEndIndex.current = WINNERS_PER_PAGE;
    setWinnersInView([...winners.slice(winnersStartIndex.current, winnersEndIndex.current)]);
  }, [winners]);

  const fetchMoreWinners = () => {
    winnersStartIndex.current = winnersStartIndex.current + WINNERS_PER_PAGE;
    winnersEndIndex.current = winnersEndIndex.current + WINNERS_PER_PAGE;
    setWinnersInView(loadedWinners => [
      ...loadedWinners,
      ...winners.slice(winnersStartIndex.current, winnersEndIndex.current),
    ]);
  };

  const reviveAdBanner = useRef();

  useEffect(() => {
    if (reviveAdBanner) {
      const reviveScript = document.createElement("script");
      reviveScript.src = "https://www.botb.com/www/delivery/asyncjs.php";
      reviveScript.async = true;
      const adContainer = document.getElementById("script_ad_server");
      if (adContainer) {
        adContainer.appendChild(reviveScript);
      }
      return () => {
        if (adContainer) {
          adContainer.innerHTML = "";
        }
      };
    }
  }, [reviveAdBanner]);

  useEffect(() => {
    const onAdsLoaded = e => {
      setHasAdsToLoad(Object.values(e.detail).some((ad: any) => Number(ad.width) > 0 || Number(ad.height) > 0));
    };
    document.addEventListener("revive-6ee315d200a003a5ac39b07d0dd34156-completed", onAdsLoaded);

    return () => {
      document.removeEventListener("revive-6ee315d200a003a5ac39b07d0dd34156-completed", onAdsLoaded);
    };
  }, []);

  return (
    <CurrentPageContext.Provider value={isWinnersPage}>
      <div id={"script_ad_server"} className={"hidden"} />
      <div ref={reviveAdBanner} className={"hidden"} />

      <article className="mt-5 lg:mt-[47px]">
        <div data-testid="winners-timeline">
          <InfiniteScroll
            dataLength={winnersInView.length}
            next={fetchMoreWinners}
            hasMore={winnersInView.length < winners.length}
            loader="">
            <VerticalTimeline lineColor="#f1f1f1" className="md:block lg:grid lg:grid-cols-2">
              {hasAdsToLoad ? (
                <>
                  {winnersInView.slice(0, 1).map((winner: PrizeCollectionWinner, index) => (
                    <TimelineElement
                      key={`${winner.Url}-${index}`}
                      index={index}
                      winner={winner}
                      playedWinnerPosition={clickedWinnerIndex}
                      setPlayedWinnerPosition={setClickedWinnerIndex}
                    />
                  ))}

                  <div className="vertical-timeline-element">
                    <VerticalTimelineElement date={"Advertising"} visible={true}>
                      <div className={`grid text-center justify-center items-center md:px-0 lg:pl-[3.7rem]`}>
                        {/*mobile card*/}
                        <ins
                          data-revive-usersubscriber={hasActiveSubscription()}
                          data-revive-zoneid="10"
                          data-revive-source="WL_card01_mob"
                          data-revive-id="6ee315d200a003a5ac39b07d0dd34156"
                          className={"lg:hidden [&:empty]:hidden"}></ins>
                        {/*desktop card*/}
                        <ins
                          data-revive-usersubscriber={hasActiveSubscription()}
                          data-revive-zoneid="9"
                          data-revive-source="WL_card01_desk"
                          data-revive-id="6ee315d200a003a5ac39b07d0dd34156"
                          className={"max-lg:hidden [&:empty]:hidden"}></ins>
                      </div>
                    </VerticalTimelineElement>
                  </div>
                  {winnersInView.slice(1).map((winner: PrizeCollectionWinner, index) => (
                    <TimelineElement
                      key={`${winner.Url}-${index}`}
                      index={index}
                      winner={winner}
                      playedWinnerPosition={clickedWinnerIndex}
                      setPlayedWinnerPosition={setClickedWinnerIndex}
                    />
                  ))}
                </>
              ) : (
                winnersInView.map((winner: PrizeCollectionWinner, index) => (
                  <TimelineElement
                    key={`${winner.Url}-${index}`}
                    index={index}
                    winner={winner}
                    playedWinnerPosition={clickedWinnerIndex}
                    setPlayedWinnerPosition={setClickedWinnerIndex}
                  />
                ))
              )}
            </VerticalTimeline>
          </InfiniteScroll>
          {winners.length === 0 && (
            <div className={styles.noFilteredWinners}>
              <img src={"/assets/images/icon-no-cars.png"} alt="NoCarSearch" />
              <h3>CHANGE YOUR SEARCH</h3>
              <p>
                Unfortunately there are no winners matching your search terms.
                <br />
                Please reset the FILTERS or search for something else.
              </p>
            </div>
          )}
        </div>
      </article>
    </CurrentPageContext.Provider>
  );
};

export default WinnersTimeline;
